import { type ClassValue, clsx } from "clsx";
import get from "lodash/get";
import { twMerge } from "tailwind-merge";
import type { ApiError, BackendValidationError, DefaultErrors } from "@/types/shared";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Extracts error messages from different backend error structures
 */
const extractErrorMessageFromBackendError = (error: unknown): string | null => {
  const responseData = get(error, "response.data") || get(error, "responseData");

  if (!responseData || typeof responseData !== "object") {
    return extractFromCommonPaths(error);
  }

  const backendError = responseData as BackendValidationError;

  const violationMessage = extractFromViolations(backendError);
  if (violationMessage) {
    return violationMessage;
  }

  if (backendError.exception) {
    return backendError.exception;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return extractFromCommonPaths(error);
};

/**
 * Extracts message from validation violations
 */
const extractFromViolations = (backendError: BackendValidationError): string | null => {
  const violationTypes = [
    backendError.parameterViolations,
    backendError.fieldViolations,
    backendError.propertyViolations,
    backendError.classViolations,
    backendError.returnValueViolations,
  ];

  for (const violations of violationTypes) {
    if (!Array.isArray(violations) || violations.length === 0) {
      continue;
    }

    const message = violations[0]?.message;
    if (message) {
      return message;
    }
  }

  return null;
};

/**
 * Extracts message from common error response paths
 */
const extractFromCommonPaths = (error: unknown): string | null => {
  const paths = [
    "response.data.message",
    "response.data.errorMessage",
    "responseData.message",
    "responseData.errorMessage",
    "response.data.error",
    "responseData.error",
  ];

  for (const path of paths) {
    const message = get(error, path);
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  return null;
};

/**
 * Handles API errors by processing different types of errors and returning a standardized error object.
 *
 * @param error - The error object to be processed
 * @param defaultErrors - Object containing default error messages for different scenarios
 * @param customMessage - Optional custom error message to override default messages
 * @returns An ApiError object containing the error message and status code
 *
 * @example
 * ```typescript
 * const defaultErrors = {
 *   responseError: 'Server error',
 *   requestError: 'Network error',
 *   unknownError: 'Unknown error'
 * };
 * const error = new Error('Custom error');
 * const result = handleApiError(error, defaultErrors);
 * ```
 *
 * @throws Nothing - This function handles all errors and returns an ApiError object
 */
const FETCH_NETWORK_ERROR_MESSAGES = [
  "Failed to fetch",
  "NetworkError when attempting to fetch resource",
  "Load failed",
];

export function isFetchNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }

  return FETCH_NETWORK_ERROR_MESSAGES.some((message) => error.message.includes(message));
}

export const handleApiError = (
  error: unknown,
  defaultErrors: DefaultErrors,
  customMessage = ""
): ApiError => {
  if (customMessage) {
    return createApiError(customMessage, error);
  }

  const backendMessage = extractErrorMessageFromBackendError(error);
  if (backendMessage) {
    return createApiError(backendMessage, error);
  }

  const standardMessage = getStandardErrorMessage(error, defaultErrors);
  return createApiError(standardMessage, error);
};

/**
 * Creates an ApiError object with message and status
 */
const createApiError = (message: string, error: unknown): ApiError => ({
  message,
  status: get(error, "response.status", 0),
});

/**
 * Gets standard error message from Error instance or fallback
 */
const getStandardErrorMessage = (error: unknown, defaultErrors: DefaultErrors): string => {
  if (error instanceof Error) {
    return error.message || defaultErrors.responseError;
  }

  return get(error, "message") || defaultErrors.unknownError;
};
